import { c as _c } from "react/compiler-runtime";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import { alpha, Box, Button, Chip, Typography, useTheme } from "@mui/material";
import React, { useMemo, useState } from "react";
import { categoryLabels } from "#/entities/category/model/categoryLabels";
import type { ProjectCategory } from "#/server/db/generated/enums";
import type { RouterOutputs } from "#/shared/api";
import { useProjectBrowserContext } from "./ProjectBrowserContext";
type ProjectBrief = RouterOutputs["project"]["allBrief"][number];
type ProjectBrowserCategoryBarProps = {
  projects: ProjectBrief[] | undefined;
};

// Max height for collapsed state (slightly taller to peek second row with fade)
const COLLAPSED_MAX_HEIGHT = 64; // Single row + peek of second

export const ProjectBrowserCategoryBar: React.FC<ProjectBrowserCategoryBarProps> = t0 => {
  const $ = _c(53);
  const {
    projects
  } = t0;
  const theme = useTheme();
  const {
    selectedCategories,
    setSelectedCategories
  } = useProjectBrowserContext();
  const [isExpanded, setIsExpanded] = useState(false);
  let t1;
  bb0: {
    if (!projects) {
      let t2;
      if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
        t2 = new Map();
        $[0] = t2;
      } else {
        t2 = $[0];
      }
      t1 = t2;
      break bb0;
    }
    let counts;
    if ($[1] !== projects) {
      counts = new Map();
      for (const project of projects) {
        const currentCount = counts.get(project.category) || 0;
        counts.set(project.category, currentCount + 1);
      }
      $[1] = projects;
      $[2] = counts;
    } else {
      counts = $[2];
    }
    t1 = counts;
  }
  const categoryCounts = t1;
  let t2;
  if ($[3] !== categoryCounts) {
    const allCategories = Array.from(categoryCounts.keys());
    t2 = allCategories.sort((categoryLeft, categoryRight) => {
      const countLeft = categoryCounts.get(categoryLeft) || 0;
      const countRight = categoryCounts.get(categoryRight) || 0;
      if (countLeft !== countRight) {
        return countRight - countLeft;
      }
      return categoryLabels[categoryLeft].localeCompare(categoryLabels[categoryRight]);
    });
    $[3] = categoryCounts;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const sortedCategories = t2;
  let t3;
  if ($[5] !== selectedCategories || $[6] !== setSelectedCategories) {
    t3 = category => {
      const isSelected = selectedCategories.includes(category);
      if (isSelected) {
        setSelectedCategories(selectedCategories.filter(selectedCategory => selectedCategory !== category));
      } else {
        setSelectedCategories([...selectedCategories, category]);
      }
    };
    $[5] = selectedCategories;
    $[6] = setSelectedCategories;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const handleCategoryClick = t3;
  let t4;
  if ($[8] !== handleCategoryClick) {
    t4 = (ev, category_0) => {
      if (ev.key === "Enter" || ev.key === " ") {
        ev.preventDefault();
        handleCategoryClick(category_0);
      }
    };
    $[8] = handleCategoryClick;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const handleKeyDown = t4;
  if (sortedCategories.length === 0) {
    return null;
  }
  let t5;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = {
      borderBottom: 1,
      borderColor: "divider"
    };
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = {
      px: 2,
      pt: 1.5,
      pb: 1
    };
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <Box sx={t6}><Typography variant="subtitle2" component="h3" sx={{
        fontWeight: 600,
        color: "text.secondary",
        fontSize: "0.75rem",
        textTransform: "uppercase",
        letterSpacing: "0.5px"
      }}>Categories</Typography></Box>;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = {
      px: 1.5
    };
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  let t9;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = {
      position: "relative"
    };
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  const t10 = isExpanded ? "none" : COLLAPSED_MAX_HEIGHT;
  const t11 = isExpanded ? "visible" : "hidden";
  let t12;
  if ($[15] !== t10 || $[16] !== t11) {
    t12 = {
      display: "flex",
      flexWrap: "wrap",
      gap: 1,
      maxHeight: t10,
      overflowY: t11,
      transition: "max-height 0.3s ease-in-out"
    };
    $[15] = t10;
    $[16] = t11;
    $[17] = t12;
  } else {
    t12 = $[17];
  }
  let t13;
  if ($[18] !== categoryCounts || $[19] !== handleCategoryClick || $[20] !== handleKeyDown || $[21] !== selectedCategories || $[22] !== sortedCategories || $[23] !== theme) {
    let t14;
    if ($[25] !== categoryCounts || $[26] !== handleCategoryClick || $[27] !== handleKeyDown || $[28] !== selectedCategories || $[29] !== theme) {
      t14 = category_1 => {
        const count = categoryCounts.get(category_1) || 0;
        const isSelected_0 = selectedCategories.includes(category_1);
        const label = categoryLabels[category_1] ?? String(category_1);
        return <Chip key={category_1} label={`${label}: ${count}`} onClick={() => handleCategoryClick(category_1)} onKeyDown={ev_0 => handleKeyDown(ev_0, category_1)} variant="filled" sx={{
          cursor: "pointer",
          backgroundColor: isSelected_0 ? theme.palette.info.main : alpha(theme.palette.info.main, 0.12),
          color: isSelected_0 ? theme.palette.common.white : theme.palette.secondary.main,
          border: `1px solid ${isSelected_0 ? theme.palette.info.main : alpha(theme.palette.secondary.main, 0.24)}`,
          "&:hover": {
            backgroundColor: isSelected_0 ? alpha(theme.palette.info.main, 0.88) : alpha(theme.palette.info.main, 0.2)
          },
          "&:focus-visible": {
            outline: "2px solid",
            outlineColor: "primary.main",
            outlineOffset: 2
          }
        }} tabIndex={0} aria-pressed={isSelected_0} aria-label={`Filter by ${label} category, ${count} projects`} />;
      };
      $[25] = categoryCounts;
      $[26] = handleCategoryClick;
      $[27] = handleKeyDown;
      $[28] = selectedCategories;
      $[29] = theme;
      $[30] = t14;
    } else {
      t14 = $[30];
    }
    t13 = sortedCategories.map(t14);
    $[18] = categoryCounts;
    $[19] = handleCategoryClick;
    $[20] = handleKeyDown;
    $[21] = selectedCategories;
    $[22] = sortedCategories;
    $[23] = theme;
    $[24] = t13;
  } else {
    t13 = $[24];
  }
  let t14;
  if ($[31] !== t12 || $[32] !== t13) {
    t14 = <Box sx={t12}>{t13}</Box>;
    $[31] = t12;
    $[32] = t13;
    $[33] = t14;
  } else {
    t14 = $[33];
  }
  let t15;
  if ($[34] !== isExpanded) {
    t15 = !isExpanded && <Box sx={{
      position: "absolute",
      bottom: 0,
      left: 0,
      right: 0,
      height: 32,
      background: _temp,
      pointerEvents: "auto"
    }} aria-hidden={true} />;
    $[34] = isExpanded;
    $[35] = t15;
  } else {
    t15 = $[35];
  }
  let t16;
  if ($[36] !== t14 || $[37] !== t15) {
    t16 = <Box sx={t9}>{t14}{t15}</Box>;
    $[36] = t14;
    $[37] = t15;
    $[38] = t16;
  } else {
    t16 = $[38];
  }
  let t17;
  if ($[39] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = {
      display: "flex",
      justifyContent: "flex-end"
    };
    $[39] = t17;
  } else {
    t17 = $[39];
  }
  let t18;
  let t19;
  if ($[40] !== isExpanded) {
    t18 = () => setIsExpanded(!isExpanded);
    t19 = isExpanded ? <ExpandLess /> : <ExpandMore />;
    $[40] = isExpanded;
    $[41] = t18;
    $[42] = t19;
  } else {
    t18 = $[41];
    t19 = $[42];
  }
  let t20;
  if ($[43] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = {
      textTransform: "none",
      minWidth: "auto",
      px: 1
    };
    $[43] = t20;
  } else {
    t20 = $[43];
  }
  const t21 = isExpanded ? "Collapse categories" : "Expand categories";
  const t22 = isExpanded ? "Collapse" : "Show more";
  let t23;
  if ($[44] !== isExpanded || $[45] !== t18 || $[46] !== t19 || $[47] !== t21 || $[48] !== t22) {
    t23 = <Box sx={t17}><Button onClick={t18} endIcon={t19} size="small" sx={t20} aria-label={t21} aria-expanded={isExpanded}>{t22}</Button></Box>;
    $[44] = isExpanded;
    $[45] = t18;
    $[46] = t19;
    $[47] = t21;
    $[48] = t22;
    $[49] = t23;
  } else {
    t23 = $[49];
  }
  let t24;
  if ($[50] !== t16 || $[51] !== t23) {
    t24 = <Box sx={t5} role="region" aria-label="Category filters">{t7}<Box sx={t8}>{t16}{t23}</Box></Box>;
    $[50] = t16;
    $[51] = t23;
    $[52] = t24;
  } else {
    t24 = $[52];
  }
  return t24;
};
function _temp(theme_0) {
  return `linear-gradient(to top, ${theme_0.palette.background.paper}, transparent)`;
}